/**
 * 移動セッション生成ユーティリティ
 *
 * 位置データ（OverlandLocation）から移動セッション（MovementSession）を生成する。
 * Visit期間中のポイントを除外し、5分以上の間隔またはVisit開始による分割を行う。
 *
 * useOverlandStore と InfiniteTimelineView の両方から利用される。
 */

import type { MovementSession, OverlandLocation, OverlandVisit } from '@/api/types/overland'
import { calculateDistance } from '@/lib/geo-utils'

/** MovementSession に隣接Visit情報を付与した拡張型（UI表示用） */
export type MovementSessionWithVisits = MovementSession & {
  /** 出発地（直前のVisit。生成時に確定） */
  departureVisit: OverlandVisit | null
  /** 到着地（直後のVisit。生成時に確定） */
  arrivalVisit: OverlandVisit | null
}

/** セッション分割の時間閾値（5分） */
const SESSION_GAP_MS = 5 * 60 * 1000

/** 最小ポイント数 */
const MIN_POINT_COUNT = 2

/** 最小移動距離（km） */
const MIN_DISTANCE_KM = 0.1

/** 隣接Visit採用の最大時間差（10分） */
const MAX_ADJACENT_GAP_MS = 10 * 60 * 1000

/**
 * 位置データから移動セッションを生成
 *
 * - Visit期間中のポイントを除外（Visit開始と同じ「分」のポイントは移動の最終ポイントとして残す）
 * - 5分以上の間隔がある場合に別セッションとして分割
 * - 2つのポイント間にVisitの開始時刻が存在する場合も分割
 * - 静止・短距離・1ポイントだけのセッションはフィルタで除外
 */
export const generateMovementSessions = (
  locations: OverlandLocation[],
  visits: OverlandVisit[] = [],
): MovementSessionWithVisits[] => {
  if (locations.length === 0) return []

  // 分単位に切り捨て（秒・ミリ秒を無視して比較するため）
  const floorToMinute = (ms: number): number => Math.floor(ms / 60_000) * 60_000

  // Visit期間を取得（arrival_date〜departure_dateの範囲、分単位）
  const visitPeriods = visits
    .filter((v) => v.arrival_date && v.departure_date)
    .map((v) => ({
      start: floorToMinute(new Date(v.arrival_date!).getTime()),
      end: floorToMinute(new Date(v.departure_date!).getTime()),
    }))
    .sort((a, b) => a.start - b.start)

  // ロケーションがVisit期間中かどうかをチェック（分単位）
  // Visit開始時刻と同じ「分」のポイントは移動の最終ポイントとして残す（start は exclusive）
  const isWithinVisit = (timestamp: string): boolean => {
    const time = floorToMinute(new Date(timestamp).getTime())
    return visitPeriods.some((period) => time > period.start && time <= period.end)
  }

  // 2つの時刻の間にVisitが存在するかチェック（分単位）
  const hasVisitBetween = (time1: number, time2: number): boolean => {
    const start = floorToMinute(Math.min(time1, time2))
    const end = floorToMinute(Math.max(time1, time2))
    return visitPeriods.some((period) => period.start >= start && period.start <= end)
  }

  // Visit期間中のロケーションを除外してソート
  const sorted = [...locations]
    .filter((loc) => !isWithinVisit(loc.timestamp))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

  if (sorted.length === 0) return []

  const firstLocation = sorted.at(0)
  if (!firstLocation) return []

  const sessions: MovementSession[] = []
  let currentSession: OverlandLocation[] = [firstLocation]

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted.at(i - 1)
    const curr = sorted.at(i)
    if (!prev || !curr) continue

    const prevTime = new Date(prev.timestamp).getTime()
    const currTime = new Date(curr.timestamp).getTime()
    const gap = currTime - prevTime

    // 分割条件:
    // 1. 5分以上の間隔がある
    // 2. 2つのポイント間にVisitの開始時刻が存在する
    const shouldSplit = gap > SESSION_GAP_MS || hasVisitBetween(prevTime, currTime)

    if (shouldSplit) {
      if (currentSession.length > 0) {
        sessions.push(createSession(currentSession, sessions.length))
      }
      currentSession = [curr]
    } else {
      currentSession.push(curr)
    }
  }

  // 最後のセッションを追加
  if (currentSession.length > 0) {
    sessions.push(createSession(currentSession, sessions.length))
  }

  // 実際の移動のみをフィルタ（静止や家の中での微妙な位置変動を除外）
  const filtered: MovementSessionWithVisits[] = sessions
    .filter((s) => {
      if (s.pointCount < MIN_POINT_COUNT) return false
      if (s.estimatedDistanceKm < MIN_DISTANCE_KM) return false
      if (s.motion === 'stationary') return false
      return true
    })
    .map((s) => ({ ...s, departureVisit: null, arrivalVisit: null }))

  // 各セッションに隣接するVisit（出発地・到着地）を埋め込む
  // Visitを時間順にソート済みの配列として使用（数値タイムスタンプを事前計算）
  const visitsWithTimes = [...visits]
    .filter((v) => v.departure_date || v.arrival_date)
    .map((v) => ({
      visit: v,
      arrivalTime: new Date(v.arrival_date ?? v.timestamp).getTime(),
      departureTime: new Date(v.departure_date ?? v.arrival_date ?? v.timestamp).getTime(),
    }))
    .sort((a, b) => a.arrivalTime - b.arrivalTime)

  // departureTime がセッション開始以下の中で最大のインデックスを二分探索
  const findBestDepartureIdx = (items: { departureTime: number }[], target: number): number => {
    let lo = 0
    let hi = items.length - 1
    let result = -1
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1
      if (items[mid]!.departureTime <= target) {
        result = mid
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }
    return result
  }

  // arrivalTime がセッション終了以上の中で最小のインデックスを二分探索
  const findBestArrivalIdx = (items: { arrivalTime: number }[], target: number): number => {
    let lo = 0
    let hi = items.length - 1
    let result = -1
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1
      if (items[mid]!.arrivalTime >= target) {
        result = mid
        hi = mid - 1
      } else {
        lo = mid + 1
      }
    }
    return result
  }

  for (const session of filtered) {
    const sessionStart = new Date(session.startTime).getTime()
    const sessionEnd = new Date(session.endTime).getTime()

    // 出発地: departureTime がセッション開始に最も近い（直前に出発した）Visit
    let bestDepartureGap = Infinity
    let bestDeparture: OverlandVisit | null = null
    const depIdx = findBestDepartureIdx(visitsWithTimes, sessionStart)
    if (depIdx !== -1) {
      const c = visitsWithTimes[depIdx]!
      bestDepartureGap = sessionStart - c.departureTime
      bestDeparture = c.visit
    }

    // 到着地: arrivalTime がセッション終了に最も近い（直後に到着した）Visit
    let bestArrivalGap = Infinity
    let bestArrival: OverlandVisit | null = null
    const arrIdx = findBestArrivalIdx(visitsWithTimes, sessionEnd)
    if (arrIdx !== -1) {
      const c = visitsWithTimes[arrIdx]!
      bestArrivalGap = c.arrivalTime - sessionEnd
      bestArrival = c.visit
    }

    session.departureVisit = bestDepartureGap <= MAX_ADJACENT_GAP_MS ? bestDeparture : null
    session.arrivalVisit = bestArrivalGap <= MAX_ADJACENT_GAP_MS ? bestArrival : null
  }

  return filtered
}

/**
 * OverlandLocationの配列からMovementSessionを生成
 */
const createSession = (locations: OverlandLocation[], index: number): MovementSession => {
  const firstLocation = locations.at(0)
  const lastLocation = locations.at(-1)
  if (!firstLocation || !lastLocation) {
    throw new Error('locations array is empty')
  }

  const startTime = firstLocation.timestamp
  const endTime = lastLocation.timestamp

  // 主な移動手段を決定（最頻出、stationaryは除外）
  const motionCounts = new Map<string, number>()
  for (const loc of locations) {
    for (const m of loc.motion) {
      motionCounts.set(m, (motionCounts.get(m) ?? 0) + 1)
    }
  }
  motionCounts.delete('stationary')

  const sortedMotions = Array.from(motionCounts.entries()).sort((a, b) => b[1] - a[1])
  const motion = sortedMotions.at(0)?.[0] ?? firstLocation.motion.at(0) ?? 'unknown'

  // 推定距離を計算
  let totalDistance = 0
  for (let i = 1; i < locations.length; i++) {
    const prev = locations.at(i - 1)
    const curr = locations.at(i)
    if (prev && curr) {
      totalDistance += calculateDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude)
    }
  }

  return {
    id: `movement-${String(index)}-${String(startTime)}`,
    startTime,
    endTime,
    motion: String(motion),
    pointCount: locations.length,
    estimatedDistanceKm: totalDistance,
    locations,
  }
}
